/**
 * Human-friendly error mapper.
 *
 * Backend detail strings can be technical or Pydantic-schema-y. This module
 * translates common patterns into plain English so lay users aren't scared.
 * All axios errors should flow through `friendlyError(err)` before display.
 */

const PATTERNS = [
  {
    re: /invalid email or password/i,
    friendly: "That email and password don't match our records. Double-check them and try again.",
  },
  {
    re: /email already registered/i,
    friendly: "This email is already registered. Try signing in instead.",
  },
  {
    re: /invalid invite code/i,
    friendly: "That invite code isn't recognized. Check with whoever invited you — codes are case-sensitive.",
  },
  {
    re: /registration is closed/i,
    friendly: "New signups are paused right now. Contact support@ds-260usa.com for an invite.",
  },
  {
    re: /file too large/i,
    friendly: "That file is too large. Please compress it or upload the pages separately (max 8MB per document, 20MB for Smart Sort).",
  },
  {
    re: /unsupported file type/i,
    friendly: "We couldn't read that file. Please upload a PDF, JPEG, PNG, or WEBP.",
  },
  {
    re: /case not found/i,
    friendly: "We couldn't find that case. It may have been deleted or you may not have access.",
  },
  {
    re: /not authenticated|401/i,
    friendly: "Your session expired. Please sign in again.",
  },
  {
    re: /network error|failed to fetch/i,
    friendly: "We couldn't reach our servers. Check your internet connection and try again in a moment.",
  },
  {
    re: /timeout|timed out/i,
    friendly: "That took longer than expected. Please try again — if the problem continues, the file may be too large.",
  },
  {
    re: /ai error|claude|classification failed/i,
    friendly: "The document reader ran into a hiccup. Please try again in a minute — if it keeps failing, contact support.",
  },
  {
    re: /invalid person_ref/i,
    friendly: "We couldn't figure out who this document belongs to. Please pick the right person from the dropdown.",
  },
  {
    re: /pdf has no readable pages/i,
    friendly: "We couldn't read any pages from that PDF. It might be password-protected or scanned at very low quality.",
  },
];

function extractRawMessage(err) {
  if (!err) return "";
  if (typeof err === "string") return err;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
  }
  if (detail?.msg) return detail.msg;
  return err.message || "";
}

export function friendlyError(err, fallback = "Something went wrong. Please try again.") {
  const raw = extractRawMessage(err);
  if (!raw) return fallback;
  for (const p of PATTERNS) {
    if (p.re.test(raw)) return p.friendly;
  }
  // Return the raw message if it's short & readable, else the fallback.
  if (raw.length < 140 && !/^\{/.test(raw)) return raw;
  return fallback;
}
